import { createSelector } from 'reselect';
import currency from 'currency.js';
import { currencySum, getAllRootToNodePaths } from 'utils/generic';
import {
  getAdjustedContentById,
  DEFAULT_EXCHANGE_RATE,
} from 'utils/stateToState';
import { accountsByIdSelector } from './accounts';
import { groupsByIdSelector } from './groups';
import {
  journalEntriesByIdSelector,
  journalEntryItemsSelector,
} from './journals';
import {
  calculateNetEr,
  convertWithExchangeRate,
  periodIdsSelector,
} from './periods';
import { settingsSelector } from './settings';

export const adjustedAccountsByIdSelector = (entryTypes) =>
  createSelector(
    accountsByIdSelector,
    journalEntriesByIdSelector,
    journalEntryItemsSelector,
    getAdjustedContentById('account', entryTypes)
  );

/*
 * Creates warning message for all accounts with duplicated field.
 */
const createMessagesDuplication = (messages, accountsById, field) => {
  let accountIdsByField = new Map();
  for (let [accountId, account] of Object.entries(accountsById)) {
    let accountIds = accountIdsByField.has(account[field])
      ? accountIdsByField.get(account[field])
      : [];
    accountIds.push(parseInt(accountId, 10));
    accountIdsByField.set(account[field], accountIds);
  }
  for (let accountIds of accountIdsByField.values()) {
    if (accountIds.length < 2) continue;
    for (let accountId of accountIds) {
      let messagesForAccount = messages.has(accountId)
        ? messages.get(accountId)
        : [];
      messagesForAccount.push(`Warning: Duplicated account ${field}.`);
      messages.set(accountId, messagesForAccount);
    }
  }
};

function getAllIdPathsToAccountsInGroups(groupsById) {
  // Paths to each group
  // Keys: group ids
  // Values: path to each group, described by group ids
  const pathsToGroupById = {};

  // Get all paths from root node in Groups Tree, described by group ids
  const groupIdPaths = getAllRootToNodePaths(0, groupsById);

  groupIdPaths.forEach((path) => {
    const lastElementInPath = path[path.length - 1];
    pathsToGroupById[lastElementInPath] = path;
  });

  return pathsToGroupById;
}

function getAccountGroupPath(accountGroupId, pathToGroup, groupsById) {
  let accountGroupPath = '';
  if (accountGroupId !== null) {
    pathToGroup.forEach((groupId) => {
      if (accountGroupPath) {
        accountGroupPath = `${accountGroupPath}/${groupsById[groupId].name}`;
      } else {
        accountGroupPath = `${groupsById[groupId].name}`;
      }
    });
  }
  return accountGroupPath;
}

const getRows = (adjustedAccountsById, groupsById, settings) => {
  // create warning messages for accounts with duplicated number or description
  const messages = new Map();
  createMessagesDuplication(messages, adjustedAccountsById, 'number');
  createMessagesDuplication(messages, adjustedAccountsById, 'description');
  const pathsToGroupById = getAllIdPathsToAccountsInGroups(groupsById);

  return Object.entries(adjustedAccountsById).map(([accountId, account]) => {
    accountId = parseInt(accountId, 10);
    const groupPath = getAccountGroupPath(
      account.groupId,
      pathsToGroupById[account.groupId],
      groupsById
    );
    const row = {
      id: accountId,
      number: account.number,
      description: account.description,
      groupId: account.groupId,
      group: account.groupId !== null ? groupsById[account.groupId].name : '',
      groupPath,
      groupsByIdPath: pathsToGroupById[account.groupId],
      messages: messages.has(accountId) ? messages.get(accountId) : [],
    };
    Object.entries(account.periods).forEach(([pId, period]) => {
      row[`unadjusted-${pId}`] = period.unadjusted;
      row[`adjusted-${pId}`] = period.adjusted || currency(0);
      const exchangeRateId = account.exchangeRateId;
      const exchangeRate = (settings?.exchangeRates || []).find(
        (exchangeRate) => exchangeRate.id === exchangeRateId
      );
      const exchangeRateForPeriod = exchangeRate
        ? exchangeRate[`er-${pId}`]
        : DEFAULT_EXCHANGE_RATE;
      row[`er-${pId}`] = exchangeRateForPeriod;
      row[`net-er-${pId}`] = calculateNetEr(row, pId);
      if (period['er-adjusted']) {
        row[`net-er-${pId}`] = currency(row[`net-er-${pId}`]).add(
          period['er-adjusted']
        );
      }
    });

    return row;
  });
};

export default createSelector(
  adjustedAccountsByIdSelector(['aje']),
  groupsByIdSelector,
  settingsSelector,
  getRows
);

const getChecksumRow = (adjustedAccountsById, periodIds, settings) => {
  const checksumRow = {
    id: -1,
    number: '',
    description: 'Checksum',
    groupId: '',
    messages: [],
  };
  periodIds.forEach((k) => {
    checksumRow[`unadjusted-${k}`] = currencySum(
      Object.values(adjustedAccountsById).map(
        (account) => account.periods[k].unadjusted
      )
    );
    // these include AJE only
    checksumRow[`adjusted-${k}`] = currencySum(
      Object.values(adjustedAccountsById).map((account) =>
        account.periods[k].hasOwnProperty('adjusted')
          ? account.periods[k].adjusted
          : currency(0)
      )
    );
    checksumRow[`net-er-${k}`] = currencySum(
      Object.values(adjustedAccountsById).map((account) => {
        const exchangeRateId = account.exchangeRateId;
        const exchangeRate = (settings?.exchangeRates || []).find(
          (exchangeRate) => exchangeRate.id === exchangeRateId
        );
        const exchangeRateForPeriod = exchangeRate
          ? exchangeRate[`er-${k}`]
          : DEFAULT_EXCHANGE_RATE;

        const net = currency(account.periods[k].unadjusted).add(
          account.periods[k].adjusted || 0
        );

        const netEr = convertWithExchangeRate(net, exchangeRateForPeriod).add(
          account.periods[k]['er-adjusted'] || 0
        );

        return netEr;
      })
    );
  });
  return [checksumRow];
};

export const checksumRowSelector = createSelector(
  adjustedAccountsByIdSelector(['aje']),
  periodIdsSelector,
  settingsSelector,
  getChecksumRow
);
